import type {
  AssignmentDTO,
  CreateAssignmentDTO,
} from "../contracts/assignments";
import { ASSIGNMENT_LINE_KEY_DEFAULT } from "./assignment-line-key";

export type AssignmentTemplateLine = {
  roleFeeKind: CreateAssignmentDTO["roleFeeKind"];
  showCount: number;
  rehearsalDays: number;
  lineKey?: string;
};

export function resolvePlannedLineKey(
  item: Pick<CreateAssignmentDTO, "lineKey">,
): string {
  if (item.lineKey?.trim()) return item.lineKey.trim();
  return ASSIGNMENT_LINE_KEY_DEFAULT;
}

export function hasAssignmentLineDuplicate(
  existing: Array<
    Pick<AssignmentDTO, "performerId" | "roleFeeKind" | "lineKey">
  >,
  performerId: string,
  roleFeeKind: CreateAssignmentDTO["roleFeeKind"],
  lineKey: string,
): boolean {
  return existing.some(
    (row) =>
      row.performerId === performerId &&
      row.roleFeeKind === roleFeeKind &&
      row.lineKey === lineKey,
  );
}

export function resolveTemplateToAssignments(
  lines: AssignmentTemplateLine[],
  performerIds: string[],
): CreateAssignmentDTO[] | { error: string } {
  const uniquePerformers = [...new Set(performerIds.filter(Boolean))];
  if (uniquePerformers.length === 0) {
    return { error: "ເລືອກຢ່າງໜ້ອຍ 1 ຄົນ" };
  }

  const result: CreateAssignmentDTO[] = [];

  for (const performerId of uniquePerformers) {
    for (const line of lines) {
      result.push({
        performerId,
        roleFeeKind: line.roleFeeKind,
        showCount: line.showCount,
        rehearsalDays: line.rehearsalDays,
        lineKey: line.lineKey,
        notes: null,
      });
    }
  }

  return result;
}

export function expandSameRuleToAssignments(
  performerIds: string[],
  payload: Omit<CreateAssignmentDTO, "performerId">,
): CreateAssignmentDTO[] | { error: string } {
  return expandMultipleRulesToAssignments(performerIds, [payload]);
}

export function expandMultipleRulesToAssignments(
  performerIds: string[],
  payloads: Array<Omit<CreateAssignmentDTO, "performerId">>,
): CreateAssignmentDTO[] | { error: string } {
  const uniquePerformers = [...new Set(performerIds.filter(Boolean))];
  if (uniquePerformers.length === 0) {
    return { error: "ເລືອກຢ່າງໜ້ອຍ 1 ຄົນ" };
  }
  if (payloads.length === 0) {
    return { error: "ເພີ່ມຢ່າງໜ້ອຍ 1 ກົດລາຄາ" };
  }

  const result: CreateAssignmentDTO[] = [];
  for (const performerId of uniquePerformers) {
    for (const payload of payloads) {
      result.push({ performerId, ...payload });
    }
  }
  return result;
}

export type BulkAssignmentSkip = {
  input: CreateAssignmentDTO;
  reason: string;
};

export function partitionBulkAssignments(
  existing: AssignmentDTO[],
  planned: CreateAssignmentDTO[],
): { toCreate: CreateAssignmentDTO[]; skipped: BulkAssignmentSkip[] } {
  const toCreate: CreateAssignmentDTO[] = [];
  const skipped: BulkAssignmentSkip[] = [];
  const seen = new Set(
    existing.map(
      (row) => `${row.performerId}:${row.roleFeeKind}:${row.lineKey}`,
    ),
  );

  for (const input of planned) {
    const lineKey = resolvePlannedLineKey(input);
    const key = `${input.performerId}:${input.roleFeeKind}:${lineKey}`;
    if (seen.has(key)) {
      skipped.push({
        input,
        reason: "ມີການຈັດນັກສະແດງບົດບາດນີ້ໃນງານແລ້ວ",
      });
      continue;
    }
    toCreate.push(input);
    seen.add(key);
  }

  return { toCreate, skipped };
}

export function countBulkAssignmentPlan(
  existing: AssignmentDTO[],
  planned: CreateAssignmentDTO[],
) {
  const { toCreate, skipped } = partitionBulkAssignments(existing, planned);
  return {
    totalPlanned: planned.length,
    newCount: toCreate.length,
    skippedCount: skipped.length,
    toCreate,
    skipped,
  };
}

export function getPerformerIdsOnEvent(assignments: AssignmentDTO[]): string[] {
  return [...new Set(assignments.map((a) => a.performerId))];
}
